import { ultimoDiaDoMes } from "./data.js";
export const TIPOS_DOCUMENTO_VISUAL = [
    "application/pdf", "image/jpeg", "image/png", "image/webp",
];
export const TIPOS_COMPROVANTE = [...TIPOS_DOCUMENTO_VISUAL, "image/heic"];
export const LIMITE_DOCUMENTO_ONBOARDING = 50 * 1024 * 1024;
export const LIMITE_COMPROVANTE = 25 * 1024 * 1024;
export function mimeDocumentoVisual(nome, mimeInformado) {
    const informado = mimeInformado.toLowerCase();
    if (TIPOS_DOCUMENTO_VISUAL.includes(informado)) {
        return informado;
    }
    const extensao = nome.toLowerCase().split(".").at(-1);
    const porExtensao = {
        pdf: "application/pdf", jpg: "image/jpeg", jpeg: "image/jpeg",
        png: "image/png", webp: "image/webp",
    };
    return porExtensao[extensao ?? ""];
}
/** Validação barata usada antes do upload e repetida no servidor. */
export function validarMetadadosArquivo(mimeType, tamanho, limite = LIMITE_DOCUMENTO_ONBOARDING, tipos = TIPOS_DOCUMENTO_VISUAL) {
    if (!tipos.includes(mimeType))
        return { valido: false, motivo: "tipo_nao_aceito" };
    if (!Number.isSafeInteger(tamanho) || tamanho <= 0) {
        return { valido: false, motivo: "arquivo_vazio" };
    }
    if (tamanho > limite)
        return { valido: false, motivo: "arquivo_grande" };
    return { valido: true };
}
function comecaCom(bytes, assinatura) {
    return assinatura.every((valor, indice) => bytes[indice] === valor);
}
/**
 * Não confia apenas no Content-Type fornecido pelo navegador ou provedor.
 * Isso não tenta provar que o documento inteiro é íntegro; impede que conteúdo
 * de outro tipo seja armazenado e enviado ao leitor como PDF/foto.
 */
export function conteudoCompativelComMime(bytes, mimeType) {
    if (mimeType === "application/pdf") {
        return comecaCom(bytes, [0x25, 0x50, 0x44, 0x46, 0x2d]); // %PDF-
    }
    if (mimeType === "image/jpeg") {
        return comecaCom(bytes, [0xff, 0xd8, 0xff]);
    }
    if (mimeType === "image/png") {
        return comecaCom(bytes, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]);
    }
    if (mimeType === "image/webp") {
        return bytes.length >= 12
            && comecaCom(bytes, [0x52, 0x49, 0x46, 0x46])
            && comecaCom(bytes.slice(8), [0x57, 0x45, 0x42, 0x50]);
    }
    if (mimeType === "image/heic") {
        if (bytes.length < 12 || !comecaCom(bytes.slice(4), [0x66, 0x74, 0x79, 0x70]))
            return false;
        const marca = String.fromCharCode(...bytes.slice(8, 12));
        return ["heic", "heix", "hevc", "hevx", "mif1", "msf1"].includes(marca);
    }
    return false;
}
export function validarArquivoVisual(bytes, mimeType, limite = LIMITE_DOCUMENTO_ONBOARDING, tipos = TIPOS_DOCUMENTO_VISUAL) {
    const metadados = validarMetadadosArquivo(mimeType, bytes.length, limite, tipos);
    if (!metadados.valido)
        return metadados;
    if (!conteudoCompativelComMime(bytes, mimeType)) {
        return { valido: false, motivo: "conteudo_incompativel" };
    }
    return { valido: true };
}
/** Converte um dia pedido pela pessoa em uma data que existe naquela competência. */
export function diaSeguroNaCompetencia(competenciaId, dia) {
    const match = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(competenciaId);
    if (!match)
        throw new Error(`competência inválida: ${competenciaId}`);
    const ano = Number(match[1]);
    const mes = Number(match[2]);
    const ultimo = ultimoDiaDoMes(ano, mes).dia;
    const inteiro = Number.isFinite(dia) ? Math.trunc(dia) : 1;
    return Math.min(ultimo, Math.max(1, inteiro));
}
/**
 * A leitura pode variar por centavos e uma guia vencida pode incluir mora.
 * Fora dessa faixa o arquivo continua guardado, mas jamais quita o mês.
 */
export function avaliarComprovanteDarf(darf, valorPago) {
    if (!darf)
        return "sem_guia";
    if (valorPago === undefined)
        return "valor_ilegivel";
    const minimo = darf.valorPrincipal * 0.99;
    const maximo = (darf.valorTotal ?? darf.valorPrincipal) * 1.35;
    return valorPago >= minimo && valorPago <= maximo ? "plausivel" : "divergente";
}
export function interpretarRespostaProposta(textoRecebido) {
    const texto = textoRecebido.toLowerCase().trim();
    if (/^(n[aã]o|cancela|cancelar|errado)\b/.test(texto))
        return { acao: "cancelar" };
    const dia = /\bdia\s*(\d{1,2})\b/.exec(texto)?.[1];
    // "sim, dia 15" é uma correção; nunca confirma silenciosamente o dia antigo.
    if (dia)
        return { acao: "corrigir_dia", dia: Number(dia) };
    if (/^(sim|pode(?:\s+sim)?|confirmo|confirmado|isso(?:\s+mesmo)?|ok|correto|certo|t[aá]\s+certo|est[aá]\s+certo)\b/.test(texto)) {
        return { acao: "confirmar" };
    }
    return { acao: "indefinida" };
}
function normalizarBusca(valor) {
    return valor.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, " ").trim();
}
/** Data civil completa e real; usada antes de escolher a competencia. */
export function dataComprovanteValida(valor) {
    const m = /^(\d{4})-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/.exec(valor);
    if (!m)
        return false;
    const ano = Number(m[1]);
    const mes = Number(m[2]);
    const dia = Number(m[3]);
    return ano >= 2000 && ano <= 2100 && dia <= ultimoDiaDoMes(ano, mes).dia;
}
export function competenciaDaDataComprovante(valor) {
    return dataComprovanteValida(valor) ? valor.slice(0, 7) : undefined;
}
/**
 * Casa a ORIGEM do pagamento com um unico inquilino. Documento exato vence;
 * sem ele, nome exato ou muito proximo precisa ter margem sobre o segundo.
 */
export function encontrarImovelDoPagador(pagadorNome, pagadorDocumento, imoveis) {
    const documento = (pagadorDocumento ?? "").replace(/\D/g, "");
    if (documento.length === 11 || documento.length === 14) {
        const porDocumento = imoveis.filter((i) => (i.inquilinoDocumento ?? "").replace(/\D/g, "") === documento);
        if (porDocumento.length === 1)
            return porDocumento[0];
    }
    const nome = normalizarBusca(pagadorNome ?? "");
    if (!nome)
        return undefined;
    const exatos = imoveis.filter((i) => normalizarBusca(i.inquilinoNome) === nome);
    if (exatos.length === 1)
        return exatos[0];
    const tokens = new Set(nome.split(" ").filter((t) => t.length >= 2));
    const pontuados = imoveis.map((imovel) => {
        const alvo = new Set(normalizarBusca(imovel.inquilinoNome)
            .split(" ").filter((t) => t.length >= 2));
        const comuns = [...tokens].filter((t) => alvo.has(t)).length;
        const score = comuns / Math.max(tokens.size, alvo.size, 1);
        return { imovel, comuns, score };
    }).sort((a, b) => b.score - a.score);
    const primeiro = pontuados[0];
    const segundo = pontuados[1];
    if (!primeiro || primeiro.comuns < 2
        || (primeiro.score < 0.72 && !(primeiro.comuns >= 3 && primeiro.score >= 0.6))) {
        return undefined;
    }
    if (segundo && primeiro.score - segundo.score < 0.15)
        return undefined;
    return primeiro.imovel;
}
/** Congela o formato completo exibido no app, inclusive categorias zeradas. */
export function deducoesComZeros(d) {
    return {
        iptu: d?.iptu ?? 0,
        condominio: d?.condominio ?? 0,
        taxaAdministradora: d?.taxaAdministradora ?? 0,
        outras: d?.outras ?? [],
    };
}
/** Resolve uma menção somente quando ela identifica um único imóvel. */
export function encontrarImovelMencionado(textoRecebido, imoveis) {
    const texto = ` ${normalizarBusca(textoRecebido)} `;
    const encontrados = imoveis.filter((imovel) => {
        const apelido = normalizarBusca(imovel.apelido);
        const primeiroNome = normalizarBusca(imovel.inquilinoNome ?? "").split(" ")[0] ?? "";
        return (apelido.length >= 2 && texto.includes(` ${apelido} `))
            || (primeiroNome.length >= 3 && texto.includes(` ${primeiroNome} `));
    });
    return encontrados.length === 1 ? encontrados[0] : undefined;
}
export function podeCasarComprovanteAutomaticamente(entrada) {
    return entrada.tipo === "recebimento"
        && !entrada.classificacaoAssumida
        && typeof entrada.valorPago === "number"
        && Boolean(entrada.dataPagamento);
}
//# sourceMappingURL=operacoesConversa.js.map